import { useEffect, useRef } from 'react';

import { checkJsonProp, CURRENCIES, i18, Payment, translationGroups, trb } from 'src/Utilities';
import { FormPreviewSwitch, OperationApproval } from 'src/Components';
import { AlertNotification } from '../../../../../shared';
import { getTrimmedValue } from '../../../../../utils';
import { PayeeVerificationMessage } from 'src/Components/PayeeVerificationMessage/PayeeVerificationMessage';
import { EnumVerifyPayeeMatch } from 'src/api';
import { getVopType } from 'src/Utilities/vop';

interface IProps {
  paymentType: string;
  data: any;
  submit: () => void;
  submitting: boolean;
  submittingSave?: boolean;
  approve?: boolean;
  cancelApprove?: () => void;
  successApprove?: () => void;
  forexPayments?: any;
  PVR: any;
  PTR: any;
  forexDraftInfo: any;
  success?: boolean;
  close?: () => void;
  resetPayment: () => void;
  edit: () => void;
  paymentSaveToSign?: () => void;
  errors?: any;
  infoData?: any;
  saveSign?: boolean;
  checkForexSubmit?: boolean;
  forexTimerValues?: any;
  noButtons?: boolean;
  RQ_ID?: string;
  AuthType?: string;
  Code?: string;
  submitSmsApprove?: () => void;
  submitMobileApprove?: () => void;
}

export const PaymentPreview = (props: IProps) => {
  const refError = useRef(null);
  const InitialAccountData = checkJsonProp(props, 'PTR.InitialAccounts');
  const DestinationAccountData = checkJsonProp(props, 'PTR.DestinationAccounts');
  const InitialAccountID = checkJsonProp(props, 'data.Payment.InitialAccountID');
  const DestinationAccountID = checkJsonProp(props, 'data.Payment.DestinationAccountID');

  const InitialAccountOne =
    InitialAccountData?.find((x: any) => x.AccountID === InitialAccountID) ?? props?.forexPayments?.InitialAccounts?.find((x: any) => x.AccountID === InitialAccountID);
  const InitialAccount = InitialAccountOne?.AccountNumber;

  const DestinationAccountOne = DestinationAccountData ? DestinationAccountData.find((x: any) => x.AccountID === DestinationAccountID) : null;
  const DestinationAccount = DestinationAccountOne?.AccountNumber;

  const PaymentsTotalAmount = props.PVR?.Payment?.AmountFormatted;
  const PaymentsTotalCurrency =
    props?.data?.Payment?.Currency ||
    checkJsonProp(
      props.PTR.InitialAccounts.find((item: any) => item.AccountID === props.PVR?.Payment?.InitialAccountID),
      'CurrencyName'
    );

  let isPaymentAvailable = true;
  let isPaymentCommissionAvailable = true;

  if (props.data?.Payment?.PaymentType === 7) {
    const currentAccountForexEUR = props?.forexPayments?.InitialAccounts?.find(
      (item: any) => getTrimmedValue(item.AccountNumber, 'all') === getTrimmedValue(InitialAccountOne?.AccountNumber, 'all') && item.CurrencyName === 'EUR'
    );

    if (InitialAccountOne?.Amounts?.[0]?.Value < props.PVR?.Payment?.Amount) {
      isPaymentAvailable = false;
    }

    if (currentAccountForexEUR?.Amounts?.[0]?.Value < props.PVR?.Payment?.CommissionFee) {
      isPaymentCommissionAvailable = false;
    }
  } else {
    if (Math.round((props.PVR?.Payment?.Amount + props.PVR?.Payment?.CommissionFee + Number.EPSILON) * 100) / 100 > InitialAccountOne?.Amounts?.[0]?.Value) {
      isPaymentAvailable = false;
    }
  }

  const isPaymentDisabled = !isPaymentCommissionAvailable || !isPaymentAvailable;

  const data = {
    ...props.PVR,
    InitialAccount,
    DestinationAccount,
    oldData: {
      ...props.data,
      Payment: {
        PaymentType: props.data?.Payment?.PaymentType ?? '2',
        IsAdditionalDataSelected: props.data?.Payment?.IsAdditionalDataSelected ?? false,
      },
    },
    forexDraftInfo: props.forexDraftInfo,
  };

  const buttons = [];
  if (props.success) {
    if (props.close) {
      buttons.push({
        title: i18.Buttons.Close,
        func: props.close,
      });
    }
    if (props.resetPayment) {
      buttons.push({
        title: i18.Buttons.NewPayment,
        func: props.resetPayment,
        props: {
          icon: null,
          type: 'primary',
        },
      });
    }
  } else {
    if (!props.approve && !props.saveSign) {
      buttons.push({
        title: i18.Buttons.Edit,
        func: props.edit,
        props: {
          icon: null,
          disabled: props.submitting || props.submittingSave || props.approve,
        },
      });
      if (
        props.paymentType !== Payment.OWN_ACCOUNTS &&
        props.paymentType !== Payment.FOREX_EXCHANGE &&
        (!data.oldData.Payment?.Currency || data.oldData.Payment.Currency === CURRENCIES.EUR)
      ) {
        buttons.push({
          title: i18.Buttons.SaveToSign,
          func: props.paymentSaveToSign,
          props: {
            icon: null,
            disabled: props.submitting,
            loading: props.submittingSave,
          },
        });
      }
      buttons.push({
        title: i18.Buttons.Approve,
        func: props.submit,
        props: {
          icon: null,
          type: 'primary',
          htmlType: 'submit',
          disabled: props.submittingSave || (props.paymentType !== Payment.OWN_ACCOUNTS && props.paymentType !== Payment.FOREX_EXCHANGE && isPaymentDisabled),
          loading: props.submitting,
        },
      });
    } else {
      buttons.push({
        title: i18.Buttons.NewPayment,
        func: () => {
          props.resetPayment();
        },
        props: {
          icon: null,
          disabled: props.submitting || props.approve,
        },
      });
    }
  }

  useEffect(() => {
    if (props.errors) {
      (refError?.current as any)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [props.errors]);

  const messageTranslation = () => {
    if (!isPaymentAvailable && !isPaymentCommissionAvailable) {
      return trb(i18.Labels.InsufficientPaymentAndCommissionFundsSaveAndSignLater);
    } else if (isPaymentCommissionAvailable) {
      return trb(i18.Labels.InsufficientFundsSaveAndSignLater);
    } else {
      return trb(i18.Labels.InsufficientCommissionFundsSaveAndSignLater);
    }
  };

  const VopResponseType = getVopType(props?.PVR?.NameMatchType, props?.PVR?.VopDetails);

  return (
    <>
      <div className="mb-3">
        {props.paymentType === Payment.NEW && VopResponseType && !props?.success && !props?.approve && (
          <PayeeVerificationMessage
            status={VopResponseType}
            matchedName={props?.PVR?.MatchedName}
          />
        )}
      </div>
      {props.paymentType !== Payment.OWN_ACCOUNTS && props.paymentType !== Payment.FOREX_EXCHANGE && isPaymentDisabled && !props.saveSign && !props.success && (
        <AlertNotification
          type="error"
          alertClassName="mb-3"
          message={messageTranslation()}
        />
      )}
      {props.infoData && props.infoData}
      {props.saveSign && (
        <AlertNotification
          type="success"
          alertClassName="mb-3"
          message={trb(i18.Labels.SaveToSignSuccesseful)}
        />
      )}
      <div ref={refError}>
        {props.errors && (
          <AlertNotification
            type="error"
            alertClassName="mb-3"
            message={trb(i18.ErrorTitles.PaymentErrors)}
            description={
              <ul>
                {props.errors.map((item: any, index: number) => (
                  <li key={index}>{trb(translationGroups.ErrorCode + item.ErrorCode)}</li>
                ))}
              </ul>
            }
          />
        )}
      </div>
      <FormPreviewSwitch
        name={props.paymentType}
        data={data}
        buttons={props.approve ? null : props.noButtons ? null : buttons}
        edit={props.edit}
        success={props.success}
        checkForexSubmit={props.checkForexSubmit as any}
        forexTimerValues={props.forexTimerValues}
        payeeMatch={EnumVerifyPayeeMatch.MTCH === props?.PVR?.NameMatchType}
        vopStatus={props?.PVR?.NameMatchType}
        vopDetails={props?.PVR?.VopDetails}
      />
      {props.approve && (
        <OperationApproval
          RQ_ID={props.RQ_ID}
          AuthType={props.AuthType}
          code={props.Code}
          cancelApprove={props.cancelApprove as any}
          successApprove={props.successApprove as any}
          submitSmsApprove={props.submitSmsApprove}
          submitMobileApprove={props.submitMobileApprove}
          approveSum={{
            sum: PaymentsTotalAmount,
            currency: PaymentsTotalCurrency,
          }}
        />
      )}
    </>
  );
};
